import React, { useState } from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { newThemeObject } from '../../newTheme'
import { formatNumber } from '../../utils/numbersFormatter'
import CardWrapper from '../common/CardWrapper'
import classNames from 'classnames'
import { AccountPosition, Position } from '../../redux/reducers/portfolio'
import { Label, LabelSecurity } from '../../redux/reducers/label'
import { useTranslation } from 'react-i18next'
import { InstrumentIcon } from '../common/InstrumentIcon'
import OrderButtons from './OrderButtons'
import { Security } from '../../redux/reducers/securities'
import DataTableRowDetails from './DataTableRowDetails'
import { hexToRGBA } from '../../utils/styles'
import { tokens } from '../../styles/tokens'
import useMobileWidth from '../../hooks/useMobileWidth'
import { useSelector } from '../../redux/hooks/useSelector'
import { IMPERIUM_FINANCE } from '../../constants/businessTerritory'
import BondSensitivityTable from './BondSensitivityTable'

// Sazby zadané zadavatelem; u ostatních měn zatím nejsou známé.
const NEGATIVE_BALANCE_DEBIT_RATES: Record<string, number> = {
  CHF: 1.25,
  USD: 4.75,
}

const useStyles = makeStyles(theme => ({
  title: {
    color: tokens.color.white,
    margin: 0,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    display: 'flex',
    alignItems: 'center',
  },
  paper: {
    overflowY: 'auto',
    overflowX: 'auto',
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  table: {
    minWidth: 1000,
  },
  totalText: {
    margin: 0,
    color: tokens.color.white,
    fontSize: '0.9375rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
  topBar: {
    background: `linear-gradient(135deg, ${tokens.color.primary[800]}, ${tokens.color.primary[600]})`,
    margin: '-16px -16px 0',
    padding: '12px 16px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} 0 0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    minHeight: '48px',
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  viewSwitch: {
    display: 'flex',
    borderRadius: tokens.radius.md,
    border: '1px solid rgba(255, 255, 255, 0.5)',
    overflow: 'hidden',
  },
  viewSwitchButton: {
    border: 'none',
    cursor: 'pointer',
    padding: '3px 10px',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    color: tokens.color.white,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.18)',
    },
  },
  viewSwitchButtonActive: {
    backgroundColor: tokens.color.white,
    color: tokens.color.primary[800],
    '&:hover': {
      backgroundColor: tokens.color.white,
    },
  },
  clickableRow: {
    cursor: 'pointer',
    transition: `background-color ${tokens.transition.fast}`,
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: tokens.color.neutral[200],
    },
    '&:hover': {
      backgroundColor: tokens.color.primary[50],
    },
  },
  firstClickableRow: {
    cursor: 'pointer',
    transition: `background-color ${tokens.transition.fast}`,
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: tokens.color.primary[50],
    },
  },
  lastRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  clickableRowExpanded: {
    background: `linear-gradient(90deg,
          ${hexToRGBA(newThemeObject.palette.main.highlight, 0)} 0%,
          ${hexToRGBA(newThemeObject.palette.main.highlight, 1)} 1%,
          ${hexToRGBA(newThemeObject.palette.main.highlight, 1)} 99%,
          ${hexToRGBA(newThemeObject.palette.main.highlight, 0)} 100%)`,
    '& .MuiTableCell-root': {
      backgroundColor: 'none',
    },
  },
  tableHead: {
    ...newThemeObject.table.tableHead,
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  tableContentFirst: {
    ...newThemeObject.table.tableContentFirst,
  },
  columnCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    color: tokens.color.neutral[900],
  },
  tableContentIcon: {
    padding: '25px 20px',
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
    fontVariantNumeric: 'tabular-nums',
  },
  amountText: {
    fontWeight: '600',
    margin: '0',
    fontSize: '0.75rem',
    fontVariantNumeric: 'tabular-nums',
    color: tokens.color.neutral[900],
  },
  // Karty pozic pro mobil - nahrazuji sirokou tabulku, aby se nemuselo nic posouvat do stran.
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  mobileCard: {
    cursor: 'pointer',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${tokens.color.neutral[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mobileCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mobileCardHeaderText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
  },
  mobileCardName: {
    margin: 0,
    fontSize: '0.8rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    overflowWrap: 'break-word',
  },
  mobileCardIsin: {
    margin: 0,
    fontSize: '0.7rem',
    color: tokens.color.neutral[500],
  },
  mobileCardAmount: {
    margin: 0,
    fontSize: '0.8rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.color.neutral[900],
    whiteSpace: 'nowrap',
  },
  mobileCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(0.75, 1.5),
    marginTop: theme.spacing(1),
  },
  mobileCardField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  mobileCardLabel: {
    margin: 0,
    fontSize: '0.65rem',
    color: tokens.color.neutral[500],
  },
  mobileCardValue: {
    margin: '2px 0 0',
    fontSize: '0.75rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'break-word',
  },
  mobileCardButtons: {
    marginTop: theme.spacing(1),
  },
}))

interface DataTableRowProps {
  selectedPortfolio: number
  portfolios: any[]
  includeSecurity: (securityId: number, label: Label) => boolean
  labelTab: number
  labels: Label[]
  securities: Security[]
  setLabelSecurity: (labelSecurity: any) => void
  clientId: string
}

type SortField = 'percent' | 'profitLoss' | 'amount' | 'portfolioCurrencyAmount' | 'currency'

interface SortState {
  field: SortField | null
  direction: 'asc' | 'desc'
}

const defaultSortState: SortState = { field: null, direction: 'asc' }

// Radi kopii pozic dane tridy aktiv - kazda trida ma vlastni SortState, takze razeni v jedne tabulce neovlivni ostatni.
const sortPositions = (items: Position[], sort: SortState): Position[] => {
  if (!sort.field) return items
  const field = sort.field
  return [...items].sort((a, b) => {
    const aVal = field === 'currency' ? a.instrument.currency : a[field]
    const bVal = field === 'currency' ? b.instrument.currency : b[field]
    const result = typeof aVal === 'string' ? aVal.localeCompare(bVal as string) : (aVal as number) - (bVal as number)
    return sort.direction === 'asc' ? result : -result
  })
}

const sortAccountPositions = (items: AccountPosition[], sort: SortState): AccountPosition[] => {
  if (!sort.field) return items
  const field = sort.field as Exclude<SortField, 'profitLoss'>
  return [...items].sort((a, b) => {
    const aVal = field === 'currency' ? a.account.currency : a[field]
    const bVal = field === 'currency' ? b.account.currency : b[field]
    const result = typeof aVal === 'string' ? aVal.localeCompare(bVal as string) : (aVal as number) - (bVal as number)
    return sort.direction === 'asc' ? result : -result
  })
}

const DataTable: React.FC<DataTableRowProps> = ({
  selectedPortfolio,
  portfolios,
  includeSecurity,
  labelTab,
  labels,
  securities,
  setLabelSecurity,
  clientId,
}) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState<number[]>([])
  const [sortByType, setSortByType] = useState<Record<string, SortState>>({})
  const { t } = useTranslation()
  const isMobile = useMobileWidth()
  const user = useSelector(state => state.auth.user)
  const isImperiumUser = !!(user && user.businessTerritoryId === IMPERIUM_FINANCE)
  const [showBondSensitivity, setShowBondSensitivity] = useState(false)

  const getSortState = (type: string): SortState => sortByType[type] || defaultSortState

  const handleSort = (type: string, field: SortField) => {
    setSortByType(prev => {
      const current = prev[type] || defaultSortState
      const direction = current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
      return { ...prev, [type]: { field, direction } }
    })
  }

  const handleRowClick = (index: number) => () => {
    setExpanded(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index)
      } else {
        return [...prev, index]
      }
    })
  }

  const getValidSecurity = (securityId: number) => {
    for (let i = 0; i < securities.length; i++) {
      if (securities[i].securityId === securityId) return securities[i]
    }
    return null
  }

  const isValidSecurity = (securityId: number) => {
    for (let i = 0; i < securities.length; i++) {
      if (securities[i].securityId === securityId) return true
    }
    return false
  }

  return (
    <>
      {portfolios[selectedPortfolio] &&
        portfolios[selectedPortfolio].assetClassPosition.map((p: any, idx: number) => {
          let tmp: Position[] = []
          let tmpSum = 0;

          if (labelTab > 0) {
            tmp = p.positions.filter((item: Position) =>
              includeSecurity(item.instrument.instrumentId, labels[labelTab - 1])
            )
            for ( let i=0; i<tmp.length; i++) {
              tmpSum +=  tmp[i].portfolioCurrencyAmount;
            }
            
          } else {
            tmp = p.positions
          }

          const sort = getSortState(p.type)
          tmp = sortPositions(tmp, sort)
          const hasSensitivitySwitch = isImperiumUser && p.type === 'BOND'
          const sensitivityActive = hasSensitivitySwitch && showBondSensitivity

          return tmp && tmp.length > 0 ? (
            <TableContainer component={CardWrapper} className={classes.paper} key={idx}>
              <span className={classes.topBar}>
                <p className={classes.title}>{t(p.type)}</p>
                <div className={classes.topBarRight}>
                  {hasSensitivitySwitch && (
                    <div className={classes.viewSwitch}>
                      <button
                        type="button"
                        className={classNames(classes.viewSwitchButton, !showBondSensitivity && classes.viewSwitchButtonActive)}
                        onClick={() => setShowBondSensitivity(false)}
                      >
                        {t('BOND_VIEW_POSITIONS')}
                      </button>
                      <button
                        type="button"
                        className={classNames(classes.viewSwitchButton, showBondSensitivity && classes.viewSwitchButtonActive)}
                        onClick={() => setShowBondSensitivity(true)}
                      >
                        {t('BOND_VIEW_SENSITIVITY')}
                      </button>
                    </div>
                  )}
                  <p className={classes.totalText}>
                    {formatNumber(labelTab>0?tmpSum:p.amount)} {portfolios[selectedPortfolio].currency}
                  </p>
                </div>
              </span>
              {sensitivityActive ? (
                <BondSensitivityTable
                  positions={tmp}
                  securities={securities}
                  currency={portfolios[selectedPortfolio].currency}
                />
              ) : isMobile ? (
                <div className={classes.mobileList}>
                  {tmp.map((position: Position) => (
                    <React.Fragment key={position.positionId}>
                      <div className={classes.mobileCard} onClick={handleRowClick(position.positionId)}>
                        <div className={classes.mobileCardHeader}>
                          <InstrumentIcon securityType={position.instrument.assetClass} />
                          <div className={classes.mobileCardHeaderText}>
                            <p className={classes.mobileCardName}>{position.instrument.name}</p>
                            <p className={classes.mobileCardIsin}>{position.instrument.isin}</p>
                          </div>
                          <p className={classes.mobileCardAmount}>
                            {formatNumber(position.portfolioCurrencyAmount)} {portfolios[selectedPortfolio].currency}
                          </p>
                        </div>
                        <div className={classes.mobileCardGrid}>
                          <div className={classes.mobileCardField}>
                            <p className={classes.mobileCardLabel}>{t('SHARE')}</p>
                            <p className={classes.mobileCardValue}>{position.percent}%</p>
                          </div>
                          <div className={classes.mobileCardField}>
                            <p className={classes.mobileCardLabel}>{t('ORDER_QUANTITY')}</p>
                            <p className={classes.mobileCardValue}>{formatNumber(position.quantity, { decimalPlaces: 0 })}</p>
                          </div>
                          <div className={classes.mobileCardField}>
                            <p className={classes.mobileCardLabel}>{t('PURCHASE_PRICE')}</p>
                            <p className={classes.mobileCardValue}>{formatNumber(position.averagePurchasePrice)}</p>
                          </div>
                          <div className={classes.mobileCardField}>
                            <p className={classes.mobileCardLabel}>{t('LAST_PRICE')}</p>
                            <p className={classes.mobileCardValue}>{formatNumber(position.lastPrice)}</p>
                          </div>
                          <div className={classes.mobileCardField}>
                            <p className={classes.mobileCardLabel}>{t('P_L')}</p>
                            <p
                              className={classes.mobileCardValue}
                              style={
                                position.profitLossPercentage > 0
                                  ? { color: tokens.color.success[700] }
                                  : { color: tokens.color.primary[500] }
                              }
                            >
                              {formatNumber(position.profitLoss) + ' (' + formatNumber(position.profitLossPercentage) + '%)'}
                            </p>
                          </div>
                          <div className={classes.mobileCardField}>
                            <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                            <p className={classes.mobileCardValue}>{formatNumber(position.amount)} {position.instrument.currency}</p>
                          </div>
                          {position.instrument.instrumentType === 'DLUHOPIS' && (
                            <div className={classes.mobileCardField}>
                              <p className={classes.mobileCardLabel}>{t('AUV')}</p>
                              <p className={classes.mobileCardValue}>
                                {formatNumber(position.lastAccrued || 0)} {position.instrument.currency}
                              </p>
                            </div>
                          )}
                          {p.type === 'MM' && NEGATIVE_BALANCE_DEBIT_RATES[position.instrument.currency] !== undefined && (
                            <div className={classes.mobileCardField}>
                              <p className={classes.mobileCardLabel}>{t('DEBIT_RATE_IG')}</p>
                              <p className={classes.mobileCardValue}>
                                {NEGATIVE_BALANCE_DEBIT_RATES[position.instrument.currency]} %
                              </p>
                            </div>
                          )}
                        </div>
                        <div className={classes.mobileCardButtons}>
                          <OrderButtons
                            securities={securities}
                            labels={labels}
                            position={position}
                            setLabelSecurity={setLabelSecurity}
                            isValidSecurity={isValidSecurity}
                            clientId={clientId}
                          />
                        </div>
                      </div>
                      {expanded.includes(position.positionId) && (
                        <DataTableRowDetails
                          position={position}
                          getValidSecurity={getValidSecurity}
                          portfolio={portfolios[selectedPortfolio]}
                          mobile
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
              <Table className={classes.table} size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])} />
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'percent'}
                        direction={sort.field === 'percent' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'percent')}
                      >
                        {t('SHARE')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('ISIN')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('NAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('ORDER_QUANTITY')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('PURCHASE_PRICE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('LAST_PRICE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'profitLoss'}
                        direction={sort.field === 'profitLoss' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'profitLoss')}
                      >
                        {t('P_L')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      {p.type === 'BOND' && <p className={classes.tableHeadChildrenText}>{t('AUV')}</p>}
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'amount'}
                        direction={sort.field === 'amount' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'amount')}
                      >
                        {t('AMOUNT')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'currency'}
                        direction={sort.field === 'currency' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'currency')}
                      >
                        {t('CURRENCY')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'portfolioCurrencyAmount'}
                        direction={sort.field === 'portfolioCurrencyAmount' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'portfolioCurrencyAmount')}
                      >
                        {t('AMOUNT_IN')} {portfolios[selectedPortfolio].currency}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      {p.type === 'MM' && <p className={classes.tableHeadChildrenText}>{t('DEBIT_RATE_IG')}</p>}
                    </TableCell>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenLast])} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tmp.map((position: Position, index: number) => (
                    <React.Fragment key={position.positionId}>
                      <TableRow
                        className={
                          expanded.includes(position.positionId)
                            ? classNames(
                                classes.clickableRowExpanded,
                                index === 0 ? classes.firstClickableRow : classes.clickableRow
                              )
                            : index === 0
                            ? classes.firstClickableRow
                            : classes.clickableRow
                        }
                        onClick={handleRowClick(position.positionId)}
                      >
                        <TableCell className={classNames(classes.tableContent, classes.tableContentFirst)}>
                          <InstrumentIcon securityType={position.instrument.assetClass} />
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position.percent}%</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position.instrument.isin}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position.instrument.name}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position.quantity, { decimalPlaces: 0 })}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position.averagePurchasePrice)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position.lastPrice)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p
                            className={classes.text}
                            style={
                              position.profitLossPercentage > 0
                                ? { color: tokens.color.success[700], fontWeight: 600 }
                                : { color: tokens.color.primary[500], fontWeight: 600 }
                            }
                          >
                            {formatNumber(position.profitLoss) + ' ('+formatNumber(position.profitLossPercentage)+'%)'}
                          </p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>
                            {position.instrument.instrumentType === 'DLUHOPIS'
                              ? `${formatNumber(position.lastAccrued || 0)} ${position.instrument.currency}`
                              : ''}
                          </p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position.amount)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position.instrument.currency}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.amountText}>{formatNumber(position.portfolioCurrencyAmount)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>
                            {p.type === 'MM' && NEGATIVE_BALANCE_DEBIT_RATES[position.instrument.currency] !== undefined
                              ? `${NEGATIVE_BALANCE_DEBIT_RATES[position.instrument.currency]} %`
                              : ''}
                          </p>
                        </TableCell>
                        <TableCell>
                          <OrderButtons
                            securities={securities}
                            labels={labels}
                            position={position}
                            setLabelSecurity={setLabelSecurity}
                            isValidSecurity={isValidSecurity}
                            clientId={clientId}
                          />
                        </TableCell>
                      </TableRow>
                      {expanded.includes(position.positionId) && (
                        <DataTableRowDetails position={position} getValidSecurity={getValidSecurity} portfolio={portfolios[selectedPortfolio]}/>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              )}
            </TableContainer>
          ) : p.accountPositions && p.accountPositions.length > 0 && labelTab === 0 ? (
            <TableContainer component={CardWrapper} className={classes.paper} key={idx}>
              <span className={classes.topBar}>
                <p className={classes.title}>{t(p.type)}</p>
                <p className={classes.totalText}>
                  {formatNumber(p.amount)} {portfolios[selectedPortfolio].currency}
                </p>
              </span>
              {isMobile ? (
                <div className={classes.mobileList}>
                  {sortAccountPositions(p.accountPositions, sort).map((accPosition: AccountPosition, index: number) => (
                    <div className={classes.mobileCard} key={index}>
                      <div className={classes.mobileCardHeader}>
                        <div className={classes.mobileCardHeaderText}>
                          <p className={classes.mobileCardName}>
                            {accPosition.account.number} {accPosition.account.currency}
                          </p>
                          <p className={classes.mobileCardIsin}>{t('ACCOUNT')}</p>
                        </div>
                        <p className={classes.mobileCardAmount}>
                          {formatNumber(accPosition.portfolioCurrencyAmount)} {portfolios[selectedPortfolio].currency}
                        </p>
                      </div>
                      <div className={classes.mobileCardGrid}>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('SHARE')}</p>
                          <p className={classes.mobileCardValue}>{accPosition.percent}%</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                          <p className={classes.mobileCardValue}>
                            {formatNumber(accPosition.amount)} {accPosition.account.currency}
                          </p>
                        </div>
                        {NEGATIVE_BALANCE_DEBIT_RATES[accPosition.account.currency] !== undefined && (
                          <div className={classes.mobileCardField}>
                            <p className={classes.mobileCardLabel}>{t('DEBIT_RATE_IG')}</p>
                            <p className={classes.mobileCardValue}>
                              {NEGATIVE_BALANCE_DEBIT_RATES[accPosition.account.currency]} %
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <Table className={classes.table} size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'percent'}
                        direction={sort.field === 'percent' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'percent')}
                      >
                        {t('SHARE')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('NAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('TYPE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'amount'}
                        direction={sort.field === 'amount' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'amount')}
                      >
                        {t('AMOUNT')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'currency'}
                        direction={sort.field === 'currency' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'currency')}
                      >
                        {t('CURRENCY')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sort.field === 'portfolioCurrencyAmount'}
                        direction={sort.field === 'portfolioCurrencyAmount' ? sort.direction : 'asc'}
                        onClick={() => handleSort(p.type, 'portfolioCurrencyAmount')}
                      >
                        {t('AMOUNT_IN')} {portfolios[selectedPortfolio].currency}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classNames(classes.tableHeadChildrenLast, classes.tableHeadChildren)}>
                      <p className={classes.tableHeadChildrenText}>{t('DEBIT_RATE_IG')}</p>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortAccountPositions(p.accountPositions, sort).map((accPosition: AccountPosition, index: number) => (
                    <TableRow key={index} className={index === p.accountPositions.length - 1 ? classes.lastRow : ''}>
                      <TableCell className={classNames(classes.tableContent, classes.tableContentFirst)}>
                        <p className={classes.text}>{accPosition.percent}%</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>
                          {accPosition.account.number} {accPosition.account.currency}
                        </p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{t('ACCOUNT')}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{formatNumber(accPosition.amount)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{accPosition.account.currency}</p>
                      </TableCell>
                      <TableCell className={classNames(classes.tableContent)}>
                        <p className={classes.amountText}>{formatNumber(accPosition.portfolioCurrencyAmount)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>
                          {NEGATIVE_BALANCE_DEBIT_RATES[accPosition.account.currency] !== undefined
                            ? `${NEGATIVE_BALANCE_DEBIT_RATES[accPosition.account.currency]} %`
                            : ''}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </TableContainer>
          ) : null
        })}
    </>
  )
}

export default DataTable
